/**
 * Backend origin with no trailing slash.
 *
 * Resolution order (first non-empty wins):
 *  1. window.__ENV__.REACT_APP_API_BASEURL  — written at container startup by entrypoint.sh
 *  2. process.env.REACT_APP_API_BASEURL     — baked in by webpack at build time (local dev)
 *
 * Prevents `https://host/` + `/api/...` → `https://host//api/...` (breaks some proxies
 * and confused path handling); pair with server-side ASGI path normalization.
 */
export function getApiOrigin(): string {
  const runtimeEnv = (window as any).__ENV__ as Record<string, string> | undefined;
  const url =
    runtimeEnv?.REACT_APP_API_BASEURL ||
    process.env.REACT_APP_API_BASEURL ||
    "";
  return url.replace(/\/+$/, "");
}

/** Same message as facilityManagement — fail fast when Hub cannot reach Rules API. */
export function requireApiOrigin(): string {
  const origin = getApiOrigin();
  if (!origin) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env (e.g. http://localhost:8000).",
    );
  }
  return origin;
}

/**
 * unicomply-documents (Document Intelligence) origin with no trailing slash.
 * Falls back to Rules origin when REACT_APP_DOCUMENTS_BASEURL is unset (local/dev).
 */
export function getDocumentsOrigin(): string {
  const runtimeEnv = (window as any).__ENV__ as Record<string, string> | undefined;
  const url =
    runtimeEnv?.REACT_APP_DOCUMENTS_BASEURL ||
    process.env.REACT_APP_DOCUMENTS_BASEURL ||
    "";
  const trimmed = url.replace(/\/+$/, "");
  return trimmed || getApiOrigin();
}

/** Fail fast when Documents API origin cannot be resolved. */
export function requireDocumentsOrigin(): string {
  const origin = getDocumentsOrigin();
  if (!origin) {
    throw new Error(
      "REACT_APP_DOCUMENTS_BASEURL is not set (and REACT_APP_API_BASEURL is empty). " +
        "Set REACT_APP_DOCUMENTS_BASEURL to the documents service origin.",
    );
  }
  return origin;
}

/**
 * Sole human identity issuer origin (affableone-identity), or MCM proxy host.
 * When unset, Hub falls back to rules-local login during migration.
 */
export function getIdentityOrigin(): string {
  const runtimeEnv = (window as any).__ENV__ as Record<string, string> | undefined;
  const url =
    runtimeEnv?.REACT_APP_IDENTITY_BASE_URL ||
    process.env.REACT_APP_IDENTITY_BASE_URL ||
    runtimeEnv?.REACT_APP_MCM_URL ||
    process.env.REACT_APP_MCM_URL ||
    "";
  return url.replace(/\/+$/, "");
}
